import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { db } from 'services/firebase';
import { AuthContext } from 'context/Auth';
import { masterChartPath } from 'constants/masterChartRoutes';
import DemographicsPage from 'pages/dashboard/individual/health-records/DemographicsPage';
import MedicationsPage from 'pages/dashboard/individual/MedicationsPage';
import AllergiesPage from 'pages/dashboard/individual/health-records/AllergiesPage';
import { MASTERCHART_INTRO, MasterChartSetupContext } from './masterChartSetupContext';

/**
 * First-visit setup: the three sections a provider needs before anything else,
 * one at a time. Deliberately not dressed as a wizard (no step bar, no "Step 1
 * of 3", no section heading): it reads as the MasterChart page showing one
 * section's form under its usual title and intro. Each step is the section's own page,
 * so there is no second copy of any form; a page reads `useMasterChartSetup()`
 * for its title and copy, to drop its back link, and to put the actions under
 * its content.
 */
const SETUP_STEPS = [
  { Page: DemographicsPage },
  { Page: MedicationsPage },
  { Page: AllergiesPage },
];

const SETUP_DONE_MSG = "Your information has been saved. You can add more history below if you'd like.";

/** Stops the hub sending this patient to setup again. */
function markMasterChartSetupSeen(uid) {
  if (!uid) return;
  db.collection('users')
    .doc(uid)
    .update({ masterChartSetupSeen: true })
    .catch((error) => console.error('Error saving MasterChart setup flag:', error));
}

const MasterChartSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const beforeContinue = useRef(null);

  const raw = Number(searchParams.get('step'));
  const step = Number.isInteger(raw) && raw >= 0 && raw < SETUP_STEPS.length ? raw : 0;
  const isLast = step === SETUP_STEPS.length - 1;
  const { Page } = SETUP_STEPS[step];

  const goToStep = (index) => {
    beforeContinue.current = null;
    setSearchParams({ step: String(index) });
  };

  const exit = (state) => {
    markMasterChartSetupSeen(user?.uid);
    navigate(masterChartPath(), { state: { setupSeen: true, ...state } });
  };

  const advance = () => (isLast ? exit({ importedMsg: SETUP_DONE_MSG }) : goToStep(step + 1));

  const handleContinue = async () => {
    if (beforeContinue.current) {
      setSaving(true);
      const ok = await beforeContinue.current();
      setSaving(false);
      if (!ok) return;
    }
    advance();
  };

  const onContinue = useCallback((fn) => {
    beforeContinue.current = fn;
  }, []);

  const footer = (
    // One row, like Save and Back on the section pages. "Show all sections" is
    // the only way out early; there is no per-step Skip, since Continue on an
    // untouched step already moves on.
    <Box sx={{ mt: 5, display: 'flex', gap: 1 }}>
      <LoadingButton variant="contained" loading={saving} onClick={handleContinue}>
        {isLast ? 'Finish' : 'Continue'}
      </LoadingButton>
      <Button onClick={() => exit()} disabled={saving}>
        Show all sections
      </Button>
    </Box>
  );

  return (
    <MasterChartSetupContext.Provider
      value={{ onContinue, advance, title: 'MasterChart', subtitle: MASTERCHART_INTRO, footer }}
    >
      <Page key={step} />
    </MasterChartSetupContext.Provider>
  );
};

export default MasterChartSetupPage;
